import React, { useEffect, useState } from "react";
import "./DetailedReport.scss";
import AiStarIcon from "../../../../utils/Images/AiStarIcon";
import { t } from "i18next";
import {
  formatScore,
  formatSummaryTitle,
  scrollWindowTop,
} from "../../../../utils/Common/Constant";
import DotBg from "../../../../utils/Images/DotBg";
import { downloadAssessmentApplicantQuestionReport } from "../../../../service/CreatedAssessmentListAndReview/CreatedAssessmentListAndReview";
import { showToast, SkeletonList } from "ui-components";
import { useParams } from "react-router-dom";
import SingleMultiTypes from "../../CreateAssessmentResponseList/QuestionType/SIngleMultiTypes";
import EssayType from "../../CreateAssessmentResponseList/QuestionType/EssayType";
import CodingType from "../../CreateAssessmentResponseList/QuestionType/CodingType";
import VideoType from "../../CreateAssessmentResponseList/QuestionType/VideoType";
import { ResponseScreenEnums } from "../../../../utils/Enums/ResponseScreenEnums";
import AiConfidenceStamp from "../../../../components/AiConfidenceStamp/AiConfidenceStamp";
import AnimatedProgressBar from "./AnimatedProgressBar";
import AnimatedCircular from "./AnimatedCircular";

const DetailedReport = () => {
  const [assessmentReportData, setAssessmentReportData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { assessmentId, applicantId } = useParams();

  // const debounceSearchText = "";
  const downloadAssessmentQuestionReport = async () => {
    setIsLoading(true);
    const getAssessmentReport: any =
      await downloadAssessmentApplicantQuestionReport(
        assessmentId,
        applicantId,
      );
    if (getAssessmentReport?.status === 200) {
      setAssessmentReportData(getAssessmentReport?.data);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    scrollWindowTop();
    downloadAssessmentQuestionReport();
  }, []);
  // Total score of Skills
  const skills = assessmentReportData?.skillScore || [];
  const totalSkills = skills.length;
  const sumScores = skills.reduce(
    (acc: number, item: any) => acc + (item?.score || 0),
    0,
  );
  const averageScore =
    totalSkills > 0 ? Math.round(sumScores / totalSkills) : 0;

  // Total score of Difficulty level
  const difficulties = assessmentReportData?.skillDIfficultyScore || [];
  const totalDifficulties = difficulties.length;
  const sumDifficultyScores = difficulties.reduce(
    (acc: number, item: any) => acc + (item?.totalscore || 0),
    0,
  );

  // average
  const averageDifficultyScore =
    totalDifficulties > 0
      ? Math.round(sumDifficultyScores / totalDifficulties)
      : 0;

  // Total score of Ai analysis
  const aiAnalysis =
    assessmentReportData?.ApplicantAssessment?.evaluationSummary?.aiAnalysis ||
    {};

  const aiValues = Object.values(aiAnalysis) as number[];

  // sirf numbers count karna
  const validScores = aiValues.filter(
    (v) => typeof v === ResponseScreenEnums.STATUS_NUMBER,
  );
  const total = validScores.reduce((acc, v) => acc + v, 0);

  const averageAiScore =
    validScores.length > 0 ? Math.round(total / validScores.length) : 0;

  // Total score of visual analysis
  const visualAnalysis =
    assessmentReportData?.ApplicantAssessment?.evaluationSummary
      ?.visualAnalysis || {};

  const visualValues = Object.values(visualAnalysis) as number[];

  const validVisualScores = visualValues.filter(
    (v) => typeof v === ResponseScreenEnums.STATUS_NUMBER,
  );

  const totalVisual = validVisualScores.reduce((acc, v) => acc + v, 0);

  const averageVisualScore =
    validVisualScores.length > 0
      ? Math.round(totalVisual / validVisualScores.length)
      : 0;

  // Color change
  const classes = ["purpleSec", "greenSec", "blueSec", "orangeSec"];
  const getRandomClass = () =>
    classes[Math.floor(Math.random() * classes.length)];

  const renderAnswerType = (type: string, answerData: any) => {
    switch (type) {
      case "Single":
        return <SingleMultiTypes answerData={answerData} />;
      case "Multiple":
        return <SingleMultiTypes answerData={answerData} />;
      case "Essay":
        return <EssayType answerData={answerData} />;
      case "Video":
        return (
          <VideoType
            answerData={answerData}
            isGridView={false}
            handleAIClick={undefined}
            isAiEvaluation={undefined}
            isPaused={false}
            isMaximize={false}
            isCustomQuestion={false}
            evaluationTypesObj={undefined}
            handleManualReview={undefined} // handleAIClick={handleAIClick}
          />
        );
      case "Coding":
        return <CodingType answerData={answerData} />;
      default:
        return null;
    }
  };
  const getRemarkClass = (remark?: string) => {
    switch (remark) {
      case "Below Par":
        return "belowPar";
      case "Good":
        return "good";
      case "Satisfactory":
        return "satisfactory";
      case "Excellent":
        return "excellent";
      default:
        return "";
    }
  };
  const keyMapping: Record<string, string> = {
    accuracy: "Correctness",
    language_and_grammar: "Communication",
  };
  const stripHtml = (html: string) => html.replace(/<[^>]+>/g, "");

  useEffect(() => {
    const accordions = document.querySelectorAll(".accordion-collapse");
    accordions.forEach((acc) => {
      acc.addEventListener("shown.bs.collapse", (e: any) => {
        e.target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });

    return () => {
      accordions.forEach((acc) => {
        acc.removeEventListener("shown.bs.collapse", () => {});
      });
    };
  }, []);
  const filterAiConfidence = (evaluationResult: any) => {
    const results = evaluationResult
      .map((item: any) => item)
      .filter((item: any) =>
        item.evaluationType ===
          ResponseScreenEnums.EVALUATION_TYPE_SMARTAI_ALL ||
        item.evaluationType ===
          ResponseScreenEnums.EVALUATION_TYPE_CORRECTNESS_VISUAL ||
        item.evaluationType ===
          ResponseScreenEnums.EVALUATION_TYPE_CORRECTNESS_AUDIO ||
        item.evaluationType === ResponseScreenEnums.EVALUATION_TYPE_CORRECTNESS
          ? item
          : null,
      );
    const aiConfidence = results[0]?.evaluation?.gemini?.result_confidence;
    return { aiConfidence, results };
  };

  return (
    <>
      {isLoading ? (
        <>
          {Array.from({ length: 5 }).map((_, index) => (
            <SkeletonList
              key={`skeleton-${index}`}
              numberOfTitles={1}
              numberofTextLines={5}
              titleSize={18}
              textSize={12}
              thumbnailHeight={250}
              thumbnailWidth={250}
              itemsGap={20}
            />
          ))}
        </>
      ) : (
        <div className="pageReport">
          <div className="assessment-report ">
            {/* Profile Section */}
            <div className="report-card">
              {/* Header */}
              <div className="profile-section">
                <div className="avatar">
                  {/* <ProfileIcon /> */}
                  <img
                    src={
                      assessmentReportData?.ApplicantAssessment?.applicantImage
                    }
                    alt=""
                  />
                </div>
                <div className="profile-info">
                  <div className="maxW">
                    <h2 className="name">
                      {assessmentReportData?.ApplicantAssessment?.displayName}
                    </h2>
                    <h6 className="title">
                      {assessmentReportData?.ApplicantAssessment?.title}
                    </h6>
                  </div>
                  <p className="contact">
                    <img src="/Images/Email.png" alt="email" />{" "}
                    {assessmentReportData?.ApplicantAssessment?.email ?? "-"}
                  </p>
                  <p className="contact">
                    <img src="/Images/mobile.png" alt="mobile" />{" "}
                    {assessmentReportData?.ApplicantAssessment?.mobile ?? "-"}
                  </p>
                </div>
                <div className="score-boxes">
                  <div className="score-box rightBr">
                    <p className="label">{t("GENERAL.CUTOFF")}</p>
                    <p className="value">
                      {assessmentReportData?.ApplicantAssessment?.cutOff ?? 0}%
                    </p>
                  </div>
                  <div className="score-box">
                    <p className="label">{t("RESPONSE_LIST.OVERALL_SCORE")}</p>
                    <p className="value">
                      {assessmentReportData?.ApplicantAssessment?.finalScore
                        ?.score ?? 0}
                      %
                    </p>
                  </div>
                </div>
              </div>

              <div className="applicantSelf">
                {/* Self Rating */}
                <div className="self-rating">
                  <h6 className="section-title">
                    {t("RESPONSE_LIST.APPLICANTS_SELF_RATING")} :
                  </h6>
                  <div className="ratings">
                    {assessmentReportData?.assessmentApplicantSkillExperience?.map(
                      (item: any, idx: any) => (
                        <span key={idx}>
                          {item?.skill?.skillName}{" "}
                          <b>{item?.experience > 0 ? item.experience : 0}</b>
                        </span>
                      ),
                    )}
                  </div>
                </div>

                {/* Comment */}
                <div className="comment-section">
                  <h6 className="section-title">
                    {t("GENERAL.REVIEW_COMMENT")}
                  </h6>
                  <p className="para">
                    {assessmentReportData?.ApplicantAssessment?.finalComment ??
                      t("GENERAL.NA")}
                  </p>
                </div>

                {/* Assessment Details */}
                <div className="assessment-details">
                  <h6 className="section-title">
                    {t("GENERAL.ASSESSMET_DETAILS")}
                  </h6>
                  <div className="detailsGrid">
                    <div className="firstCol">
                      <div className="firstRow">
                        <p>{t("RESUME.ASSESSMENT_QUESTIONS")}</p>
                        <p>
                          {assessmentReportData?.ApplicantAssessment
                            ?.totalQuestions ?? 0}
                        </p>
                      </div>
                      <div className="firstRow">
                        <p>{t("GENERAL.CUTOFF")}</p>
                        <p>
                          {assessmentReportData?.ApplicantAssessment?.cutOff ??
                            0}
                          %
                        </p>
                      </div>
                      <div className="firstRow">
                        <p>{t("GENERAL.TIME")}</p>
                        <p>
                          {assessmentReportData?.ApplicantAssessment
                            ?.totalTime ?? 0}{" "}
                          {t("GENERAL.MINS")}
                        </p>
                      </div>
                    </div>
                    <div className="secondCol">
                      <div className="firstRow">
                        <p>{t("GENERAL.RESULT_STATUS")}</p>
                        <p>
                          {assessmentReportData?.ApplicantAssessment
                            ?.resultStatus ?? "-"}
                        </p>
                      </div>
                      <div className="firstRow">
                        <p>{t("GENERAL.SKILLS_TITLE")}</p>
                        <p>
                          {assessmentReportData?.assessmentApplicantSkillExperience?.map(
                            (item: any, idx: any) => (
                              <span key={idx}>
                                {item?.skill?.skillName ?? "-"},{" "}
                              </span>
                            ),
                          )}
                        </p>
                      </div>
                      <div className="firstRow">
                        <p>{t("GENERAL.REMARK")}</p>
                        <p
                          className={`remarkBullet ${getRemarkClass(
                            assessmentReportData?.ApplicantAssessment?.remark,
                          )}`}
                        >
                          {assessmentReportData?.ApplicantAssessment?.remark}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Overall Sec */}
            <div className="overallSec">
              <h2 className="section-title">
                <div>
                  <DotBg />
                </div>
                <div>
                  {t("RESPONSE_LIST.OVERALL_SCORE")}
                  <span>
                    {assessmentReportData?.ApplicantAssessment?.finalScore
                      ?.score ?? 0}
                    %
                  </span>
                </div>
                <div className="reverseDotBg">
                  <DotBg />
                </div>
              </h2>
            </div>

            {/* Category Section */}
            {assessmentReportData?.categoryScore?.map(
              (category: any, idx: number) => {
                const matchedSkills =
                  assessmentReportData?.assessmentSkills
                    ?.filter(
                      (skill: any) =>
                        skill.categoryId === category.skillsCategoryId,
                    ) // filter by category
                    .filter(
                      (skill: { skillName: any }, index: any, self: any[]) =>
                        index ===
                        self.findIndex((s) => s.skillName === skill.skillName),
                    ) || [];

                return (
                  <div key={idx}>
                    <div className={`AnalysisSec ${getRandomClass()}`}>
                      <div className="d-flex justify-content-between align-items-center analysisHeader">
                        <h2 className="section-title">
                          {category?.categoryName}
                        </h2>
                        <div className="scoreOutOff">
                          {formatScore(category?.totalScore)}
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-4">
                          {matchedSkills.length > 0 ? (
                            matchedSkills.map((skill: any) => {
                              // find skill score from skillScore array
                              const skillScore =
                                assessmentReportData?.skillScore?.find(
                                  (s: any) => s.key === skill?.skillName,
                                )?.score ?? 0; // fallback to 0 if not found

                              return (
                                <div key={skill.skillId} className="mb-3">
                                  <div className="d-flex gap-3 align-items-center mb-1">
                                    <span className="skillBullet">
                                      {skill?.skillName}
                                    </span>
                                    <p>
                                      <b>{skillScore}%</b>
                                    </p>
                                  </div>
                                  <AnimatedProgressBar
                                    skillScore={skillScore}
                                  />
                                </div>
                              );
                            })
                          ) : (
                            <p className="text-muted">
                              {t("GENERAL.NO_SKILLS_FOUND")}
                            </p>
                          )}
                        </div>

                        <div className="col-md-8">
                          <div className="summary">
                            {Object.entries(category?.summary || {}).map(
                              ([key, value]) => (
                                <div className="mb-4" key={key}>
                                  <li>
                                    <div className="liDiv">
                                      <div>{formatSummaryTitle(key)}</div>
                                      <p className="summaryPara">
                                        {(value as string) ?? t("GENERAL.NA")}
                                      </p>
                                    </div>
                                  </li>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              },
            )}

            {/* Skills */}
            <div className={`AnalysisSec ${getRandomClass()}`}>
              <div className="d-flex justify-content-between align-items-center analysisHeader">
                <h2 className="section-title">{t("GENERAL.SKILL_SCORE")}</h2>
                <div className="scoreOutOff">{formatScore(averageScore)}</div>
              </div>
              <div className="row">
                {(assessmentReportData?.skillScore || []).map(
                  (item: any, idx: number) => (
                    <div className="col-md-6 col-lg-4" key={idx}>
                      <div className="d-flex gap-5 align-items-center mb-5 barDiv">
                        <span className="skillBullet">{item?.key}</span>
                        <AnimatedCircular percent={item?.score ?? 0} />
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>

            {/* Difficulty Skill Experience */}
            <div className={`AnalysisSec ${getRandomClass()}`}>
              <div className="d-flex justify-content-between align-items-center analysisHeader">
                <h2 className="section-title">
                  {t("GENERAL.DIFFICULTY_LEVEL_WISE")}
                </h2>
                <div className="scoreOutOff">
                  {formatScore(averageDifficultyScore)}
                </div>
              </div>
              <div className="row">
                {(assessmentReportData?.skillDIfficultyScore || []).map(
                  (item: any, idx: number) => (
                    <div className="col-md-6 col-lg-4" key={idx}>
                      <div className="d-flex gap-5 align-items-center mb-5 barDiv">
                        <span className="skillBullet">
                          {item?.difficultyLevel}
                        </span>
                        <AnimatedCircular percent={item?.totalscore ?? 0} />
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>

            {/* AI Analysis */}
            <div className={`AnalysisSec ${getRandomClass()}`}>
              <div className="d-flex justify-content-between align-items-center analysisHeader">
                <h2 className="section-title">
                  {t("CREATE_ASSESSMENT.LABEL_AI_ANALYSIS_EVALUATION")}
                </h2>
                <div className="scoreOutOff">{formatScore(averageAiScore)}</div>
              </div>
              <div className="row">
                {Object.entries(
                  assessmentReportData?.ApplicantAssessment?.evaluationSummary
                    ?.aiAnalysis || {},
                )
                  .filter(([key]) => key !== "aiConfidence") // remove aiConfidence
                  .map(([key, value], idx: number) => {
                    const displayKey =
                      keyMapping[key] || key.replace(/_/g, " ");
                    return (
                      <div className="col-md-4" key={idx}>
                        <div className="d-flex justify-content-between align-items-center">
                          <p>
                            {displayKey.charAt(0).toUpperCase() +
                              displayKey.slice(1)}
                          </p>
                          <p>
                            <b>
                              {typeof value ===
                              ResponseScreenEnums.STATUS_NUMBER
                                ? Number(value)
                                : 0}
                              %
                            </b>
                          </p>
                        </div>
                        <AnimatedProgressBar
                          skillScore={
                            typeof value === ResponseScreenEnums.STATUS_NUMBER
                              ? (value as number)
                              : 0
                          }
                        />
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Visual Analysis */}
            <div className={`AnalysisSec ${getRandomClass()}`}>
              <div className="d-flex justify-content-between align-items-center analysisHeader">
                <h2 className="section-title">
                  {t("RESPONSE_LIST.VISUAL_ANALYSIS")}
                </h2>
                <div className="scoreOutOff">
                  {formatScore(averageVisualScore)}
                </div>
              </div>
              <div className="row">
                {Object.entries(
                  assessmentReportData?.ApplicantAssessment?.evaluationSummary
                    ?.visualAnalysis || {},
                ).map(([key, value], idx: number) => (
                  <div className="col-md-4" key={idx}>
                    <div className="d-flex justify-content-between align-items-center">
                      <p>{formatSummaryTitle(key)}</p>
                      <p>
                        <b>
                          {typeof value === ResponseScreenEnums.STATUS_NUMBER
                            ? Number(value)
                            : 0}
                          %
                        </b>
                      </p>
                    </div>

                    <AnimatedProgressBar
                      skillScore={
                        typeof value === ResponseScreenEnums.STATUS_NUMBER
                          ? (value as number)
                          : 0
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Voice Analysis */}
            <div className="row">
              <div className="col-md-6">
                <div
                  className={`AnalysisSec voiceAnalysis ${getRandomClass()}`}
                >
                  <div className="d-flex justify-content-between align-items-center analysisHeader">
                    <h2 className="section-title">
                      {t("RESPONSE_LIST.VOICE_ANALYSIS")}
                    </h2>
                    <div className="scoreOutOff">
                      {formatScore(
                        assessmentReportData?.ApplicantAssessment
                          ?.evaluationSummary?.voiceAnalysis,
                      )}
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center barDiv">
                    <h2 className="skillBullet">
                      {t("RESPONSE_LIST.VOICE_ANALYSIS")}
                    </h2>

                    <AnimatedCircular
                      percent={
                        assessmentReportData?.ApplicantAssessment
                          ?.evaluationSummary?.voiceAnalysis ?? 0
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Communication Analysis */}
              <div className="col-md-6">
                <div
                  className={`AnalysisSec voiceAnalysis ${getRandomClass()}`}
                >
                  <div className="d-flex justify-content-between align-items-center analysisHeader">
                    <h2 className="section-title">
                      {t("GENERAL.COMMUNICATION_ANALYSIS")}
                    </h2>
                    <div className="scoreOutOff">
                      {formatScore(
                        assessmentReportData?.ApplicantAssessment
                          ?.evaluationSummary?.aiAnalysis?.language_and_grammar,
                      )}
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center barDiv">
                    <h2 className="skillBullet">
                      {t("GENERAL.COMMUNICATION_ANALYSIS")}
                    </h2>

                    <AnimatedCircular
                      percent={
                        assessmentReportData?.ApplicantAssessment
                          ?.evaluationSummary?.aiAnalysis
                          ?.language_and_grammar ?? 0
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Question Sec */}
            <div className="QuestionSec">
              <div className="accordion" id="accordionExample">
                {assessmentReportData?.data?.map((item: any, ind: number) => {
                  const isSkipped = item?.status?.toLowerCase() === "skipped"; // normalize check

                  const confidence = filterAiConfidence(item?.evaluationResult);
                  return (
                    <div className="accordion-item" key={ind}>
                      {/* {item?.status== skipped } */}
                      <h2 className="accordion-header" id={`heading-${ind}`}>
                        <button
                          className={`accordion-button ${isSkipped ? "collapsed" : ""}`}
                          type="button"
                          data-bs-toggle={isSkipped ? undefined : "collapse"}
                          data-bs-target={
                            isSkipped ? undefined : `#collapse-${ind}`
                          }
                          aria-expanded={
                            isSkipped ? "false" : ind === 0 ? "true" : "false"
                          }
                          aria-controls={`collapse-${ind}`}
                          onClick={() => {
                            if (isSkipped) {
                              showToast(t("ERROR.SKIPPED_QUESTION"));
                            } else {
                              setTimeout(() => {
                                const header = document.getElementById(
                                  `heading-${ind}`,
                                );
                                header?.scrollIntoView({
                                  behavior: "smooth",
                                  block: "nearest",
                                });
                              }, 300);
                            }
                          }}
                        >
                          <div className="questionHeader d-flex justify-content-between w-100">
                            <div className="questionText">
                              <div className="typeStatus">
                                <div
                                  className={`bulletSec ${isSkipped ? "skippedBgClr" : ""}`}
                                >
                                  <div className="questionSkillName">
                                    {item?.isCustomQuestion
                                      ? t("GENERAL.CUSTOM_QUESTION")
                                      : `${item?.skillName} - ${item?.difficultyLevel}`}
                                  </div>
                                </div>

                                <div className="d-flex align-items-center">
                                  {t("GENERAL.TYPE")} :
                                  <span className="answerType">
                                    {item?.type}
                                  </span>
                                  {item?.isCustomQuestion === false && (
                                    <span className="reportedAiIcon d-flex align-items-center ms-2 gap-2">
                                      <div
                                        className={`${isSkipped ? "skippedClr" : ""}`}
                                      >
                                        {(item?.type === "Video" ||
                                          item?.type === "coding") && (
                                          <AiStarIcon
                                            className={`me-1 ${isSkipped ? "text-gray" : "text-warning"}`}
                                          />
                                        )}
                                      </div>
                                      {isSkipped && (
                                        <>
                                          |
                                          <span className="statusTextSkip">
                                            {item?.status}
                                          </span>
                                        </>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div>
                                <span>
                                  {String(item.questionSequence).padStart(
                                    2,
                                    "0",
                                  )}
                                  .
                                </span>
                                <span>{stripHtml(item.question)}</span>
                              </div>
                            </div>
                            <div className="questionMeta d-flex align-items-center">
                              <div className="text-center">
                                <div className="d-flex align-items-center gap-4">
                                  <div className="position-relative">
                                    {item.type === "Video"
                                      ? item.status !== "skipped" && (
                                          <div className="assessScore">
                                            {confidence?.aiConfidence !==
                                            undefined ? (
                                              <AiConfidenceStamp
                                                score={confidence?.aiConfidence}
                                                width="120"
                                                height="150"
                                              />
                                            ) : (
                                              ""
                                            )}
                                          </div>
                                        )
                                      : ""}
                                  </div>
                                  <div>
                                    <div className="d-flex justify-content-center gap-1">
                                      {item?.scoreType !== "auto" && (
                                        <h6>
                                          {item?.scoreType === null
                                            ? ""
                                            : item?.scoreType === "smartAi"
                                              ? `${t("RESPONSE_LIST.AI")}`
                                              : `${t("GENERAL.MANUAL")}`}
                                        </h6>
                                      )}
                                      <h6>{t("GENERAL.SCORE")}</h6>
                                    </div>
                                    <h4
                                      className={`generalScore ${isSkipped ? "skippedClr" : ""}`}
                                    >
                                      {item?.totalScore === null ||
                                      item?.totalScore === undefined
                                        ? "-"
                                        : `${item.totalScore}%`}
                                    </h4>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </button>
                      </h2>
                      <div
                        id={`collapse-${ind}`}
                        className={`accordion-collapse collapse ${ind === 0 ? "show" : ""}`}
                        data-bs-parent="#accordionExample"
                      >
                        <div className="accordion-body">
                          {renderAnswerType(item?.type, item)}
                          {item?.type == "Video" ? (
                            <>
                              <div className="row">
                                {/* Gemini section */}
                                <div className="col-md-12">
                                  <div className="aiDetailsContent">
                                    {(() => {
                                      const geminiEval =
                                        item?.evaluationResult?.find(
                                          (ev: any) => ev.evaluation,
                                        )?.evaluation?.gemini || {};
                                      const orderedKeys = [
                                        geminiEval?.positivity
                                          ? "positivity"
                                          : geminiEval?.accuracy
                                            ? "accuracy"
                                            : null,
                                        "completeness",
                                        "clarity",
                                        "relevance",
                                        "depth",
                                        "communication",
                                        "visual_analysis",
                                        "voice_analysis",
                                      ].filter(Boolean);

                                      return orderedKeys.map(
                                        (orderedKey: any) => {
                                          const value = geminiEval[orderedKey];
                                          if (!value) return null;

                                          const displayKey =
                                            keyMapping[orderedKey] ||
                                            orderedKey.replace(/_/g, " ");

                                          return (
                                            <div
                                              key={orderedKey}
                                              className="mb-4"
                                            >
                                              <h5>
                                                {displayKey
                                                  .charAt(0)
                                                  .toUpperCase() +
                                                  displayKey.slice(1)}{" "}
                                                - {value?.percentage ?? 0}%
                                              </h5>
                                              <h6>
                                                <b>
                                                  {t(
                                                    "RESPONSE_LIST.AI_ANALYSIS",
                                                  )}{" "}
                                                  :
                                                </b>{" "}
                                                {value?.reasoning
                                                  ? value?.reasoning
                                                  : t("GENERAL.NA")}
                                              </h6>
                                              <h6>
                                                <b>
                                                  {t("RESPONSE_LIST.LACKING")} :
                                                </b>{" "}
                                                {value?.deduction_reason
                                                  ? value?.deduction_reason
                                                  : t("GENERAL.NA")}
                                              </h6>
                                            </div>
                                          );
                                        },
                                      );
                                    })()}
                                  </div>
                                </div>
                                {/* // Visual analysis (SmartAi) section  */}
                                <div className="col-md-12">
                                  <div className="mb-4">
                                    <h5 className="analysisSecTitle">
                                      {t("RESPONSE_LIST.VISUAL_ANALYSIS")} -{" "}
                                      <span>
                                        {(confidence?.results[0]
                                          ?.evaluationType ===
                                          ResponseScreenEnums.EVALUATION_TYPE_SMARTAI_ALL ||
                                          confidence?.results[0]
                                            ?.evaluationType ===
                                            ResponseScreenEnums.EVALUATION_TYPE_CORRECTNESS_VISUAL) &&
                                        confidence?.results[0]?.evaluation
                                          ?.smartAi &&
                                        confidence?.results[0]?.evaluation
                                          ?.smartAi?.face_emotions_cont &&
                                        confidence?.results[0]?.evaluation
                                          ?.smartAi?.face_emotions_cont !==
                                          null &&
                                        confidence?.results[0]?.evaluation
                                          ? Math.round(
                                              confidence?.results[0]?.evaluation
                                                ?.smartAi?.face_emotions_cont?.[
                                                "Emotion Averages"
                                              ]?.CALM,
                                            ) + "%"
                                          : t("GENERAL.NA")}
                                      </span>
                                    </h5>

                                    {/* // Face Emotions  */}
                                    <div className="row">
                                      {Object.entries(
                                        item?.evaluationResult?.find(
                                          (ev: any) => ev.evaluation,
                                        )?.evaluation?.smartAi
                                          ?.face_emotions_cont?.[
                                          "Emotion Averages"
                                        ] || {},
                                      )
                                        .filter(
                                          ([emotion]) =>
                                            !["SMIRK", "NEUTRAL"].includes(
                                              emotion,
                                            ),
                                        ) // 👈 skip these
                                        .map(
                                          ([emotion, value]: [string, any]) => (
                                            <div
                                              className="col-md-3"
                                              key={emotion}
                                            >
                                              <div>
                                                <h6 className="emotionHeading">
                                                  {emotion
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                    emotion
                                                      .slice(1)
                                                      .toLowerCase()}{" "}
                                                  -{" "}
                                                  <span>
                                                    <b>{Math.round(value)}</b>%
                                                  </span>
                                                </h6>
                                              </div>
                                            </div>
                                          ),
                                        )}
                                    </div>
                                  </div>
                                </div>
                                {/* // Voice Section  */}
                                <div className="col-md-12">
                                  <div className="mb-4">
                                    <h5 className="analysisSecTitle">
                                      {t("RESPONSE_LIST.VOICE_ANALYSIS")} -{" "}
                                      <span>
                                        {confidence?.results[0]?.evaluation
                                          ?.smartAi &&
                                        confidence?.results[0]?.evaluation
                                          ?.smartAi?.voice_confidence !==
                                          undefined &&
                                        confidence?.results[0]?.evaluation
                                          ?.smartAi?.voice_confidence !==
                                          null &&
                                        confidence?.results[0]?.evaluation
                                          ? Math.round(
                                              confidence?.results[0]?.evaluation
                                                ?.smartAi?.voice_confidence *
                                                100,
                                            ) + "%"
                                          : t("GENERAL.NA")}
                                      </span>
                                    </h5>
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            ""
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Final Recommendation */}
            <div className="finalRecommendation">
              <div className="row">
                <div className="col-md-12">
                  <h3>
                    <strong>{t("GENERAL.FINAL_RECOMMENDATION")}</strong>
                  </h3>
                </div>
                <div className="col-md-6 brRight">
                  <p className="title positiveFeed">
                    {t("GENERAL.POSITIVE_FEEDBACK")}
                  </p>
                  <ul>
                    {Object.entries(
                      assessmentReportData?.finalRecommendation || {},
                    ).map(([, item]: [string, any], i) =>
                      item?.positive_feedback ? (
                        <li key={i}>{item?.positive_feedback}</li>
                      ) : null,
                    )}
                  </ul>
                </div>
                <div className="col-md-6">
                  <p className="title improveFeed">
                    {t("GENERAL.AREA_OF_IMPROVEMENT")}
                  </p>
                  <ul>
                    {Object.entries(
                      assessmentReportData?.finalRecommendation || {},
                    ).map(([, item]: [string, any], i) =>
                      item?.area_of_improvement ? (
                        <li key={i}>{item?.area_of_improvement}</li>
                      ) : null,
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DetailedReport;
